// src/features/roles/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";
import type { Role, RoleDTO } from "./types";

/* =========================
 * Server shapes (as returned by your API)
 * ========================= */

/**
 * Minimal server role shape we care about on the frontend.
 * Backend may still send more fields (status, audit columns, etc.),
 * but we intentionally ignore them and only map what the UI needs.
 */
type ServerRole = {
  id: number;
  name: string;
  code?: string | null;
  description?: string | null;
  isSystemRole?: boolean;
};

type ApiEnvelope<T> = {
  ok: boolean;
  message?: string;
  data: T;
};

type ServerPage<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

/* =========================
 * Small helpers
 * ========================= */
function unwrap<T>(res: any): T {
  // Supports { ok, data } envelope OR plain object.
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function asMessage(err: unknown): Error {
  const e = err as any;
  const msg =
    e?.response?.data?.message ||
    e?.message ||
    "Request failed";
  return new Error(msg);
}

/**
 * Map server role → UI Role model.
 * Note: we intentionally ignore any status/audit fields here.
 */
function toRoleModelFromServer(r: ServerRole): Role {
  return {
    id: r.id,
    name: r.name,
    code: r.code ?? null,
    description: r.description ?? null,
    isSystemRole: r.isSystemRole ?? false,
  };
}

/**
 * Payload mapper for update.
 * Only send fields the backend expects for create/update.
 * No status / isActive / audit fields from the UI.
 */
function toServerUpdatePayload(payload: RoleDTO.Update): {
  name?: string;
  code?: string | null;
  description?: string | null;
  isSystemRole?: boolean;
} {
  return {
    name: payload.name,
    code: payload.code ?? null,
    description: payload.description ?? null,
    ...(payload.isSystemRole !== undefined
      ? { isSystemRole: payload.isSystemRole }
      : {}),
  };
}

/**
 * Payload mapper for create.
 */
function toServerCreatePayload(payload: RoleDTO.Create): {
  name: string;
  code?: string | null;
  description?: string | null;
  isSystemRole?: boolean;
} {
  return {
    name: payload.name,
    code: payload.code ?? null,
    description: payload.description ?? null,
    ...(payload.isSystemRole !== undefined
      ? { isSystemRole: payload.isSystemRole }
      : {}),
  };
}

/* =========================
 * URLs (from constants)
 * ========================= */
const base = PATHS.roles.base;
const byId = (id: number | string) => `${base}/${id}`;
const grantsUrl = (id: number | string) => `${base}/${id}/grants`;
const permissionsUrl = `${base}/permissions`;

/* =========================
 * API calls
 * ========================= */

/** List roles with pagination + filters.
 *  NOTE: no status filter here — backend is responsible for
 *  returning only active / non-deleted roles.
 */
export async function listRoles(
  params: RoleDTO.ListParams = {}
): Promise<RoleDTO.Page<Role>> {
  try {
    const { page = 1, pageSize = 20, q } = params;
    const qp = new URLSearchParams();
    qp.set("page", String(page));
    qp.set("pageSize", String(pageSize));
    if (q) qp.set("q", q);

    const res = await request<ApiEnvelope<ServerPage<ServerRole>> | ServerPage<ServerRole>>({
      method: "GET",
      url: `${base}?${qp.toString()}`,
    });

    const pageBody = unwrap<ServerPage<ServerRole>>(res);

    return {
      items: pageBody.items.map(toRoleModelFromServer),
      total: pageBody.total,
      page: pageBody.page,
      pageSize: pageBody.pageSize,
    };
  } catch (err) {
    throw asMessage(err);
  }
}

/** Get one role */
export async function getRole(id: number): Promise<Role> {
  try {
    const res = await request<ApiEnvelope<ServerRole> | ServerRole>({
      method: "GET",
      url: byId(id),
    });
    const body = unwrap<ServerRole>(res);
    return toRoleModelFromServer(body);
  } catch (err) {
    throw asMessage(err);
  }
}

/** Create role */
export async function createRole(payload: RoleDTO.Create): Promise<Role> {
  try {
    const res = await request<ApiEnvelope<ServerRole> | ServerRole>({
      method: "POST",
      url: base,
      data: toServerCreatePayload(payload),
    });
    const body = unwrap<ServerRole>(res);
    return toRoleModelFromServer(body);
  } catch (err) {
    throw asMessage(err);
  }
}

/** Update role */
export async function updateRole(
  id: number,
  payload: RoleDTO.Update
): Promise<Role> {
  try {
    const res = await request<ApiEnvelope<ServerRole> | ServerRole>({
      method: "PUT",
      url: byId(id),
      data: toServerUpdatePayload(payload),
    });
    const body = unwrap<ServerRole>(res);
    return toRoleModelFromServer(body);
  } catch (err) {
    throw asMessage(err);
  }
}

/** Delete role — once deleted, it's gone (no soft-delete handling in UI). */
export async function deleteRole(id: number): Promise<{ ok: boolean }> {
  try {
    const res = await request<ApiEnvelope<{ ok: boolean }> | { ok: boolean }>({
      method: "DELETE",
      url: byId(id),
    });
    const body = unwrap<{ ok: boolean }>(res);
    return body && typeof body.ok === "boolean" ? body : { ok: true };
  } catch (err) {
    throw asMessage(err);
  }
}

/* =========================
 * Role Grants (Permissions)
 * ========================= */

export async function getRoleGrants(
  roleId: number
): Promise<RoleDTO.RoleGrantsResponse> {
  try {
    const res = await request<
      ApiEnvelope<RoleDTO.RoleGrantsResponse> | RoleDTO.RoleGrantsResponse
    >({
      method: "GET",
      url: grantsUrl(roleId),
    });
    return unwrap<RoleDTO.RoleGrantsResponse>(res);
  } catch (err) {
    throw asMessage(err);
  }
}

export async function updateRoleGrants(
  roleId: number,
  payload: RoleDTO.UpdateGrantsPayload
): Promise<{ ok: boolean }> {
  try {
    const res = await request<ApiEnvelope<{ ok: boolean }> | { ok: boolean }>({
      method: "PUT",
      url: grantsUrl(roleId),
      data: payload,
    });
    const body = unwrap<{ ok: boolean }>(res);
    return body && typeof body.ok === "boolean" ? body : { ok: true };
  } catch (err) {
    throw asMessage(err);
  }
}

/* =========================
 * (Optional) Permission catalog
 * ========================= */
export async function listPermissions(): Promise<RoleDTO.Permission[]> {
  try {
    const res = await request<
      ApiEnvelope<RoleDTO.Permission[]> | RoleDTO.Permission[]
    >({
      method: "GET",
      url: permissionsUrl,
    });
    return unwrap<RoleDTO.Permission[]>(res);
  } catch (err) {
    throw asMessage(err);
  }
}
